import { z } from "zod";

import {
  DEFAULT_SKILL,
  DEFAULT_SOURCE,
  type SetupMode,
  type SetupOptions,
  SETUP_RUNNERS,
  SETUP_SCOPES,
} from "./types.js";

const SetupRunnerSchema = z.enum(SETUP_RUNNERS);
const SetupScopeSchema = z.enum(SETUP_SCOPES);

const BaseOptionsSchema = z.object({
  agents: z.array(z.string().trim().min(1)).optional(),
  copy: z.boolean().default(false),
  help: z.boolean().default(false),
  runner: SetupRunnerSchema.default("auto"),
  scope: SetupScopeSchema.default("global"),
  skill: z.string().trim().min(1).default(DEFAULT_SKILL),
  source: z.string().trim().min(1).default(DEFAULT_SOURCE),
  systemPromptAgents: z.array(z.string().trim().min(1)).optional(),
  yes: z.boolean(),
});

type RawSetupArgs = {
  options: Record<string, boolean | string | string[]>;
  positionals: string[];
};

export function buildSetupHelpText(mode: SetupMode) {
  const yesDefault = mode === "upgrade" ? "yes" : "no";

  return `Usage:
  emergent-thinking ${mode}
  emergent-thinking ${mode} -a codex -a claude-code
  emergent-thinking ${mode} --project
  emergent-thinking ${mode} --runner bunx

Defaults:
  source: ${DEFAULT_SOURCE}
  skill: ${DEFAULT_SKILL}
  scope: global
  yes: ${yesDefault}

Options:
  --source <repo>       Override the source passed to skills add
  -s, --skill <name>    Select a specific skill inside the source repo
  -a, --agent <name>    Repeat to target specific agents
  --agents <csv>        Comma-separated form of --agent
  --agents-system-prompt <csv|none>
                        Comma-separated managed prompt targets
  -r, --runner <name>   auto | npx | bunx | pnpx
  -g, --global          Install skills globally (default)
  --project             Install skills into the current project
  --copy                Pass through --copy to skills add
  -y, --yes             Skip installer confirmations
  -h, --help            Show ${mode} help`;
}

export function parseSetupArgs(mode: SetupMode, argv: string[]): SetupOptions {
  const raw = parseRawSetupArgs(argv);
  const normalized = normalizeSetupOptions(raw.options);
  const agents = [
    ...readStringList(normalized, "agent"),
    ...readCsvList(normalized, "agents"),
  ];
  const systemPromptAgents = normalizeSystemPromptAgents(
    readCsvList(normalized, "agents-system-prompt"),
  );
  const hasYes = hasOption(normalized, "yes");
  const scope = readBoolean(normalized, "project") ? "project" : "global";

  const parsed = BaseOptionsSchema.parse({
    agents: agents.length === 0 ? undefined : agents,
    copy: readBoolean(normalized, "copy"),
    help: readBoolean(normalized, "help"),
    runner: readString(normalized, "runner"),
    scope,
    skill: readString(normalized, "skill"),
    source: readString(normalized, "source") ?? raw.positionals[0],
    systemPromptAgents,
    yes: hasYes ? readBoolean(normalized, "yes") : mode === "upgrade",
  });

  return {
    ...parsed,
    agents: parsed.agents,
    systemPromptAgents: parsed.systemPromptAgents,
  };
}

function parseRawSetupArgs(argv: string[]): RawSetupArgs {
  const options: Record<string, boolean | string | string[]> = {};
  const positionals: string[] = [];

  for (let index = 0; index < argv.length; index += 1) {
    const token = argv[index];
    if (token === undefined) {
      break;
    }
    if (!token.startsWith("-") || token === "-") {
      positionals.push(token);
      continue;
    }

    const trimmed = token.startsWith("--") ? token.slice(2) : token.slice(1);
    const separator = trimmed.indexOf("=");
    if (separator >= 0) {
      assignOption(options, trimmed.slice(0, separator), trimmed.slice(separator + 1));
      continue;
    }

    const next = argv[index + 1];
    if (next !== undefined && !next.startsWith("-")) {
      assignOption(options, trimmed, next);
      index += 1;
      continue;
    }
    assignOption(options, trimmed, true);
  }

  return { options, positionals };
}

function normalizeSetupOptions(options: Record<string, boolean | string | string[]>) {
  const aliases: Record<string, string> = {
    a: "agent",
    g: "global",
    h: "help",
    r: "runner",
    s: "skill",
    y: "yes",
  };
  const normalized: Record<string, boolean | string | string[]> = {};
  for (const [key, value] of Object.entries(options)) {
    const target = aliases[key] ?? key;
    assignOption(normalized, target, value);
  }
  return normalized;
}

function assignOption(
  options: Record<string, boolean | string | string[]>,
  key: string,
  value: boolean | string | string[],
) {
  const current = options[key];
  if (current === undefined) {
    options[key] = value;
    return;
  }
  if (Array.isArray(current)) {
    current.push(...(Array.isArray(value) ? value : [String(value)]));
    return;
  }
  options[key] = Array.isArray(value)
    ? [String(current), ...value.map((item) => String(item))]
    : [String(current), String(value)];
}

function hasOption(options: Record<string, boolean | string | string[]>, key: string) {
  return Object.prototype.hasOwnProperty.call(options, key);
}

function readBoolean(options: Record<string, boolean | string | string[]>, key: string) {
  const value = options[key];
  if (value === undefined) {
    return false;
  }
  if (typeof value === "boolean") {
    return value;
  }
  if (Array.isArray(value)) {
    return readBoolean({ [key]: value.at(-1) ?? false }, key);
  }
  return value !== "false";
}

function readString(options: Record<string, boolean | string | string[]>, key: string) {
  const value = options[key];
  if (typeof value === "string") {
    return value;
  }
  if (Array.isArray(value)) {
    const last = value.at(-1);
    return typeof last === "string" ? last : undefined;
  }
  return undefined;
}

function readStringList(options: Record<string, boolean | string | string[]>, key: string) {
  const value = options[key];
  if (value === undefined) {
    return [];
  }
  if (Array.isArray(value)) {
    return value.flatMap((entry) =>
      typeof entry === "string" && entry.length > 0 ? [entry] : [],
    );
  }
  return typeof value === "string" && value.length > 0 ? [value] : [];
}

function readCsvList(options: Record<string, boolean | string | string[]>, key: string) {
  return readStringList(options, key).flatMap((entry) =>
    entry
      .split(",")
      .map((value) => value.trim())
      .filter((value) => value.length > 0),
  );
}

function normalizeSystemPromptAgents(values: string[]) {
  if (values.length === 0) {
    return undefined;
  }
  if (values.length === 1 && values[0]?.toLowerCase() === "none") {
    return [];
  }
  return values;
}
